import {
  Area,
  AreaChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
  type TooltipProps,
} from 'recharts';

import type { LiveSample } from '@/hooks/useLiveMetrics';
import { fmtClock } from '@/lib/format';

const AXIS = { stroke: '#3a4660', fontSize: 11, fontFamily: 'IBM Plex Mono, monospace' };
const GRID = 'rgba(150,180,220,0.08)';

function TipBox({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: 'rgba(8,11,17,0.95)',
        border: '1px solid var(--line-2)',
        borderRadius: 8,
        padding: '8px 10px',
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: 12,
      }}
    >
      <div className="muted" style={{ marginBottom: 4 }}>{fmtClock(Number(label))}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color }}>
          {p.name}: {Number(p.value).toFixed(2)}
        </div>
      ))}
    </div>
  );
}

const xProps = {
  dataKey: 't',
  type: 'number' as const,
  domain: ['dataMin', 'dataMax'] as [string, string],
  scale: 'time' as const,
  tickFormatter: fmtClock,
  tick: AXIS,
  axisLine: { stroke: GRID },
  tickLine: false,
  minTickGap: 48,
};

export function ThroughputChart({ samples }: { samples: LiveSample[] }) {
  // Prefer the clean per-direction split (new agents) — Download/Upload is what
  // an operator actually wants, and it's unambiguous on a bidirectional test.
  // Fall back to the role-based TX/RX lines for legacy sessions that predate
  // the split fields.
  const hasDir = samples.some((s) => s.download != null || s.upload != null);
  const series = hasDir
    ? [
        { name: 'Download Mbps', dataKey: 'download', stroke: '#5cc8ff', fill: 'url(#rxFill)' },
        { name: 'Upload Mbps', dataKey: 'upload', stroke: '#54f0a8', fill: 'url(#txFill)' },
      ]
    : [
        { name: 'TX Mbps', dataKey: 'tx', stroke: '#54f0a8', fill: 'url(#txFill)' },
        { name: 'RX Mbps', dataKey: 'rx', stroke: '#5cc8ff', fill: 'url(#rxFill)' },
      ];
  return (
    <ResponsiveContainer width="100%" height={240}>
      <AreaChart data={samples} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
        <defs>
          <linearGradient id="txFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#54f0a8" stopOpacity={0.45} />
            <stop offset="100%" stopColor="#54f0a8" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="rxFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5cc8ff" stopOpacity={0.35} />
            <stop offset="100%" stopColor="#5cc8ff" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis {...xProps} />
        <YAxis
          tick={AXIS}
          axisLine={false}
          tickLine={false}
          width={46}
          unit=""
          tickFormatter={(v: number) => String(v)}
        />
        <Tooltip content={<TipBox />} />
        {series.map((s) => (
          <Area
            key={s.dataKey}
            type="monotone"
            name={s.name}
            dataKey={s.dataKey}
            stroke={s.stroke}
            strokeWidth={2}
            fill={s.fill}
            isAnimationActive={false}
            dot={false}
            connectNulls
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function LatencyLossChart({ samples }: { samples: LiveSample[] }) {
  // Drop timeline (Wifi8 special): one marker per second that lost packets,
  // pinned to the bottom of the plot as a "rug", dot size ∝ packets dropped.
  // This shows WHEN drops happened alongside the smoothed per-second loss %.
  const dropPoints = samples
    .filter((s) => s.dropCount != null && s.dropCount > 0)
    .map((s) => ({ t: s.t, dropY: 0, n: s.dropCount as number }));

  return (
    <ResponsiveContainer width="100%" height={180}>
      <ComposedChart data={samples} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis {...xProps} />
        {/* Left axis: latency (large ms values). Right axis: loss % and TCP
            retransmit count, which are small and would be invisible if forced
            onto the latency scale. */}
        <YAxis yAxisId="lat" tick={AXIS} axisLine={false} tickLine={false} width={46} />
        <YAxis
          yAxisId="rt"
          orientation="right"
          tick={AXIS}
          axisLine={false}
          tickLine={false}
          width={40}
        />
        {/* Hidden axis for the drop rug — fixed 0..1 domain pins markers to the
            baseline regardless of the latency scale. */}
        <YAxis yAxisId="drops" hide domain={[0, 1]} />
        <ZAxis type="number" dataKey="n" range={[24, 220]} name="Dropped pkts" />
        <Tooltip content={<TipBox />} />
        <Line
          yAxisId="lat"
          type="monotone"
          name="Latency ms (down)"
          dataKey="latency"
          stroke="#ffc24b"
          strokeWidth={2}
          isAnimationActive={false}
          dot={false}
          connectNulls
        />
        <Line
          yAxisId="lat"
          type="monotone"
          name="Latency ms (up)"
          dataKey="latencyUp"
          stroke="#ff9d4b"
          strokeWidth={2}
          strokeDasharray="4 3"
          isAnimationActive={false}
          dot={false}
          connectNulls
        />
        <Line
          yAxisId="rt"
          type="monotone"
          name="Loss %"
          dataKey="loss"
          stroke="#ff5d6c"
          strokeWidth={2}
          isAnimationActive={false}
          dot={false}
          connectNulls
        />
        <Line
          yAxisId="rt"
          type="monotone"
          name="Retransmits/s"
          dataKey="retransmits"
          stroke="#b98cff"
          strokeWidth={2}
          isAnimationActive={false}
          dot={false}
          connectNulls
        />
        <Scatter
          yAxisId="drops"
          name="Drop events"
          data={dropPoints}
          dataKey="dropY"
          fill="#ff5d6c"
          fillOpacity={0.75}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// Distinct per-class colours. Ordered so the first few stay legible together —
// a QoS mix is usually 2-3 classes, and those are the ones that must contrast.
const TYPE_COLORS = ['#5cc8ff', '#54f0a8', '#ffd166', '#ff8fa3', '#c39bff', '#7fe7d4'];

/**
 * Throughput split by traffic class, with optional markers for the moments a
 * finding refers to.
 *
 * This is the chart a QoS mix actually needs: the merged Download/Upload lines
 * average across classes, so a starved voice stream disappears into a healthy
 * total. Here it shows up as its own flat line.
 */
export function TrafficTypeChart({
  rows,
  types,
  marks = [],
}: {
  rows: Record<string, number>[];
  types: string[];
  marks?: { t: number; label: string; color: string }[];
}) {
  if (rows.length === 0 || types.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <ComposedChart data={rows} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis {...xProps} />
        <YAxis tick={AXIS} axisLine={{ stroke: GRID }} tickLine={false} width={44} />
        <Tooltip content={<TipBox />} />
        {marks.map((m, i) => (
          <ReferenceLine
            key={`${m.t}-${i}`}
            x={m.t}
            stroke={m.color}
            strokeDasharray="3 3"
            label={{ value: m.label, position: 'top', fill: m.color, fontSize: 10 }}
          />
        ))}
        {types.map((ty, i) => (
          <Line
            key={ty}
            type="monotone"
            dataKey={ty}
            name={ty}
            stroke={TYPE_COLORS[i % TYPE_COLORS.length]}
            strokeWidth={1.8}
            dot={false}
            isAnimationActive={false}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
